import { Divider, Grid, Link, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Dispatch, SetStateAction, useRef } from 'react'
import { IoOpenOutline } from 'react-icons/io5'
import { Navigate, useLocation } from 'react-router-dom'
import CPFLogo from '../assets/CPFLogo.png'
import { Formtitle, Layout } from '../components'
import CpfStatusPreConfiguredRegistration from '../components/cpf/CpfStatusPreConfiguredRegistration'
import { isCpfDocumentsSignaturePending } from '../utils/misc'
import { FormType } from '../utils/sharedTypes'

type CPFRedirectPropsType = {
  form: FormType
  setForm: Dispatch<SetStateAction<FormType>>
}

const CPFRedirect = ({ form, setForm }: CPFRedirectPropsType) => {
  const tutorialRef = useRef<HTMLIFrameElement>(null)
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.down('lg'))
  const location = useLocation()

  if (isCpfDocumentsSignaturePending(form.data.cpf_documents_signature)) {
    return <Navigate to={{ pathname: '../cpf-signature', search: location.search }} replace />
  }

  return (
    <Layout form={form.data} steps={form.steps}>
      {form.data.registration_type === 'SelfRegistration' ? (
        <>
          <Formtitle title="Finalisation de votre inscription" />
          <div>
            <Grid
              sx={{
                mt: 2,
              }}
            >
              <Typography variant="body1" fontWeight="500">
                Découvrez toutes les étapes pour finaliser l'inscription à votre formation
              </Typography>
              <Typography variant="caption">
                N'hésitez pas à{' '}
                <Link
                  onClick={() => {
                    tutorialRef.current?.scrollIntoView({
                      behavior: 'smooth',
                    })
                  }}
                >
                  visualiser le tuto vidéo de pré-inscription Mon Compte Formation - CPF
                </Link>
              </Typography>
            </Grid>

            <Grid container direction="column" sx={{ mt: 4 }}>
              <Grid container direction="row" wrap="nowrap">
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    minHeight: '40px',
                    borderRadius: '4px',
                    background: theme.palette.primary.light,
                    mr: 2.5,
                  }}
                >
                  <Typography variant="body1" fontWeight="500">
                    1
                  </Typography>
                </Grid>

                <Grid container direction="column">
                  <Typography variant="body1" fontWeight="500">
                    Accéder à la formation {form.data.product?.name} sur Mon Compte Formation
                  </Typography>

                  <Grid container direction="row" wrap="nowrap" mt={2} alignItems="center">
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-around"
                      sx={{
                        background: theme.palette.info.light,
                        border: `1px solid ${theme.palette.info.main}`,
                        borderRadius: '4px',
                        height: 60,
                        width: {
                          md: 340,
                          xs: '100%',
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <Link
                        href={`https://www.moncompteformation.gouv.fr/espace-prive/html/#/formation/recherche/${
                          form.data.product?.cpf_action_string ?? ''
                        }/${form.data.product?.cpf_action_string ?? ''}`}
                        target="_blank"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          textAlign: 'center',
                          mx: 2,
                        }}
                      >
                        {form.data.product?.name}
                        <IoOpenOutline
                          size={24}
                          style={{
                            marginLeft: 10,
                            marginBottom: 4,
                          }}
                        />
                      </Link>
                    </Grid>

                    {!isLargeScreen && (
                      <img
                        src={CPFLogo}
                        alt="Logo CPF"
                        style={{
                          height: 40,
                          objectFit: 'contain',
                          marginLeft: 20,
                        }}
                      />
                    )}
                  </Grid>

                  <Divider sx={{ my: 2.5 }} />
                </Grid>
              </Grid>

              <Grid container direction="row" wrap="nowrap" alignItems={!isLargeScreen ? 'baseline' : 'inherit'}>
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    minHeight: '40px',
                    borderRadius: '4px',
                    background: theme.palette.primary.light,
                    mr: 2.5,
                  }}
                >
                  <Typography variant="body1" fontWeight="500">
                    2
                  </Typography>
                </Grid>

                <Grid container direction="column">
                  <Typography variant="body1" fontWeight="500">
                    Connectez-vous sur votre CPF, à l'aide de FranceConnect+ et l'Identité Numérique La Poste
                  </Typography>
                  <Divider sx={{ my: 2.5 }} />
                </Grid>
              </Grid>
              <Grid container direction="row" wrap="nowrap" alignItems={!isLargeScreen ? 'baseline' : 'inherit'}>
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    minHeight: '40px',
                    borderRadius: '4px',
                    background: theme.palette.primary.light,
                    mr: 2.5,
                  }}
                >
                  <Typography variant="body1" fontWeight="500">
                    3
                  </Typography>
                </Grid>

                <Grid container direction="column">
                  <Typography variant="body1" fontWeight="500">
                    De retour sur le site du CPF, cliquez sur "Demande de devis" en bas à droite de la page
                  </Typography>

                  <Divider sx={{ my: 2.5 }} />
                </Grid>
              </Grid>

              <Grid container direction="row" wrap="nowrap">
                <Grid
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    minHeight: '40px',
                    borderRadius: '4px',
                    background: theme.palette.primary.light,
                    mr: 2.5,
                  }}
                >
                  <Typography variant="body1" fontWeight="500">
                    4
                  </Typography>
                </Grid>

                <Grid container direction="column">
                  <Typography variant="body1" fontWeight="500">
                    Acceptez les conditions et finalisez votre pré-inscription pour suivre la formation{' '}
                    {form.data.product?.name}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2.5 }} />
              <Grid sx={{ mt: 2 }}>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{
                    mb: 1,
                  }}
                >
                  Vous êtes perdu(e) ? Visionnez le tutoriel vidéo ci-dessous !
                </Typography>
                <Typography variant="body2">Pour vous accompagner pas à pas dans votre inscription</Typography>
              </Grid>
              <Grid item className="container" sx={{ mt: 2.5 }}>
                <iframe
                  style={{
                    borderRadius: '4px',
                  }}
                  height="100%"
                  width="100%"
                  src="https://iframe.mediadelivery.net/embed/252149/de90802f-4563-4670-bb6c-5ddcaf387641"
                  frameBorder="0"
                  ref={tutorialRef}
                />
              </Grid>
              <Divider sx={{ my: 3 }} />
            </Grid>
          </div>
        </>
      ) : (
        <>
          <Formtitle title="Finalisation de votre inscription" />
          <CpfStatusPreConfiguredRegistration form={form} setForm={setForm} />
        </>
      )}
    </Layout>
  )
}

export default CPFRedirect
